import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MapPreviewComponent } from '../map-preview/map-preview';

@Component({
  selector: 'app-event-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, MapPreviewComponent],
  template: `
    <div class="bg-gray-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group h-full flex flex-col">
      <div class="h-48 bg-gray-300 relative overflow-hidden shrink-0 group/slider z-0">
        
        <!-- Slide 0: Cover Image -->
        <div class="absolute inset-0 transition-opacity duration-300" [class.opacity-0]="currentSlide !== 0" [class.opacity-100]="currentSlide === 0">
            <img [src]="event.image_url || '/assets/event-placeholder.png'" [alt]="event.title_it" class="w-full h-full object-cover object-[center_75%] group-hover:scale-105 transition-transform duration-500">
            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 z-0"></div>
        </div>

        <!-- Slide 1: Map (if available) -->
        @if (event.strava_map_polyline) {
            <div class="absolute inset-0 bg-gray-100 transition-opacity duration-300" [class.opacity-0]="currentSlide !== 1" [class.opacity-100]="currentSlide === 1">
                <app-map-preview [polyline]="event.strava_map_polyline" [distance_km]="event.distance_km" *ngIf="currentSlide === 1 || mapLoaded"></app-map-preview>
            </div>
        }

        <!-- Slider Controls -->
        @if (event.strava_map_polyline) {
            <!-- Arrows - Visible on hover -->
            <button (click)="prevSlide($event)" class="absolute left-3 top-1/2 -translate-y-1/2 bg-black/70 text-white rounded-full p-2 hover:bg-black/90 transition-all shadow-lg opacity-0 group-hover/slider:opacity-100 z-20">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button (click)="nextSlide($event)" class="absolute right-3 top-1/2 -translate-y-1/2 bg-black/70 text-white rounded-full p-2 hover:bg-black/90 transition-all shadow-lg opacity-0 group-hover/slider:opacity-100 z-20">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>

            <!-- Dots -->
            <div class="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-20">
                <div class="w-2 h-2 rounded-full bg-white transition-opacity" [class.opacity-50]="currentSlide !== 0"></div>
                <div class="w-2 h-2 rounded-full bg-white transition-opacity" [class.opacity-50]="currentSlide !== 1"></div>
            </div>
        }
        


        <!-- Difficulty Badge (Always visible, top-right) -->
        <div class="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg z-30"
             [ngClass]="{
               'text-green-600': event.difficulty === 'easy',
               'text-orange-500': event.difficulty === 'medium',
               'text-red-600': event.difficulty === 'hard'
             }">
          {{ 'EVENTS.DIFFICULTY_' + (event.difficulty | uppercase) | translate }}
        </div>

        <!-- Sold Out Overlay -->
        @if (event.current_participants >= event.max_participants) {
          <div class="absolute inset-0 bg-black/60 flex items-center justify-center z-10 transition-all duration-300 group-hover:bg-black/70 pointer-events-none">
            <img src="/assets/sold-out.png" alt="SOLD OUT" class="w-3/4 max-w-[200px] transform -rotate-12 drop-shadow-lg filter brightness-110">
          </div>
        }
      </div>
      <div class="p-6 flex flex-col flex-grow">
        <div class="flex justify-between items-start mb-2">
            <div class="text-sm text-enduro-orange font-bold uppercase tracking-wide">
              {{ event.event_date | date:'longDate' }}
            </div>
            <div class="flex gap-2">
                @if (event.distance_km) {
                    <span class="text-[10px] font-black text-gray-300 uppercase tracking-tighter">{{ event.distance_km }}km</span>
                }
                @if (event.elevation_gain) {
                    <span class="text-[10px] font-black text-gray-300 uppercase tracking-tighter">{{ event.elevation_gain }}m D+</span>
                }
            </div>
        </div>
        <h3 class="text-xl font-bold mb-2 text-gray-900 group-hover:text-enduro-orange transition-colors">
          {{ translate.currentLang === 'en' ? (event.title_en || event.title_it) : event.title_it }}
        </h3>
        <p class="text-gray-600 mb-4 line-clamp-3 flex-grow text-sm leading-relaxed">
          {{ translate.currentLang === 'en' ? (event.description_en || event.description_it) : event.description_it }}
        </p>
        
        <div class="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
          <div class="flex items-center text-xs font-bold text-gray-400 truncate max-w-[200px]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-enduro-orange opacity-50" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
            </svg>
            {{ event.location }}
          </div>
          
          @if (event.current_participants < event.max_participants) {
              <button (click)="onBook()" class="bg-enduro-orange text-white text-[10px] font-black px-5 py-2 rounded-xl uppercase tracking-widest hover:bg-black transition-all shadow-lg hover:shadow-orange-200">
                {{ 'EVENTS.BOOKING.BOOK_NOW' | translate }}
              </button>
          } @else {
              <span class="text-red-500 text-[10px] font-black uppercase tracking-widest">Sold Out</span>
          }
        </div>
      </div>

      <!-- 3D Modal -->

    </div>
  `
})
export class EventCardComponent {
  @Input() event: any;
  @Output() book = new EventEmitter<any>();


  public translate = inject(TranslateService);

  currentSlide = 0;
  mapLoaded = false;


  nextSlide(e: Event) {
    e.stopPropagation();
    this.currentSlide = 1;
    this.mapLoaded = true;
  }

  prevSlide(e: Event) {
    e.stopPropagation();
    this.currentSlide = 0;
  }

  onBook() {
    this.book.emit(this.event);
  }
}
